import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { X } from "../overview/icons";
import { Tooltip } from "../../design-system/components/Tooltip/Tooltip";
import { BarsSkeleton, QueryBoundary } from "../../components/dashboard";
import {
  agentKey,
  formatCompact,
  formatDateTimeUTC,
  useAgentScope,
  useErrorBreakdown,
  useErrors,
  UNTAGGED_AGENT,
  type ErrorAgentRow,
  type ErrorBreakdownRow,
  type ErrorRow,
  type Meta,
} from "../../analytics";

/* Failure drill-down (OPSS-352) — what the "N failed" qualifier on the Spend
 * card actually is: which upstream errors, from which provider, for which
 * agent, and the individual calls behind them.
 *
 * Mounted from WorkspaceHome (workspace-wide) and AgentDetailPage (inside its
 * AgentScopeProvider, so useScopedQuery pins every read to that agent). Both
 * reads are live view scans with no rollup behind them, so nothing is fetched
 * until the drawer is actually open — never alongside the dashboard.
 *
 * Ships BEFORE the BFF that populates `provider`/`error_type`, so every render
 * path has to survive those fields being absent and the breakdown endpoint not
 * existing yet. Styled in the `.tw-app` "Claude Design" layer (the drawer is a
 * child of the app shell, so its tokens resolve without re-declaring them). */

/** Rows per page of the per-call table. */
const PAGE_SIZE = 25;

/** What the synthetic `(untagged)` bucket is — the overview's roster row
 *  carries the same explanation. */
const UNTAGGED_NOTE =
  "Failures the gateway received with no x-agent-id, so they can't be attributed to an agent. They are counted in this workspace's failed-call total.";

/** Why an agent cell is blank. Distinct from {@link UNTAGGED_NOTE}: that one is
 *  a fact about the call, this one is a limit of the data behind this response. */
const AGENT_UNKNOWN_NOTE =
  "Agent attribution isn't available for this range yet — the failure list is being served from a source that doesn't carry it.";

/** Slice selected by clicking a breakdown row; narrows both reads.
 *
 *  `status` and `provider` are the only two facets the wire declares (BFF
 *  `ERRORS_APPLIED`) — sending an undeclared param instead comes back as a bare
 *  400, which this client reads as an auth failure. The breakdown groups more
 *  finely than that (by error type and signature too), so one facet can cover
 *  several rows of that list. Rather than pretend otherwise, the panel names
 *  the population it selected and lights up every row inside it. */
interface Facet {
  status?: number;
  provider?: string;
}

/* Both reads have a degraded path the BFF takes deliberately rather than
 * failing: it answers from raw OTLP, which carries no agent attribution and no
 * groupable error envelope. It reports that by leaving the filter it could not
 * apply out of `meta.filters`, and by returning empty breakdown dimensions over
 * a live `total`. Neither shape is an error, and neither means "nothing
 * failed" — so the panel names what is missing instead of answering a question
 * it wasn't served. */
const SCOPE_UNAVAILABLE =
  "Per-agent failure detail isn't available for this range yet. The workspace overview lists every failure.";
const DIMENSION_UNAVAILABLE =
  "This breakdown isn't available for this range. The individual calls are listed below.";

/** Did the server actually apply the agent scope this panel is mounted under?
 *
 *  `/errors` and `/errors/breakdown` accept `agent_id`, but only their
 *  view-backed path applies it; the fallback answers workspace-wide and says so
 *  by pruning the filter from `meta.filters`. On the agent page that difference
 *  is the whole meaning of the drawer — a workspace-wide list under one agent's
 *  "12 failed" is every other agent's failures — so the panel trusts the echo,
 *  never the request it sent. */
function scopeApplied(meta: Meta, agentId: string | null): boolean {
  return (
    agentId === null || (meta.filters.agent_id?.includes(agentId) ?? false)
  );
}

/** Hover text for the agent cell. Only the two ambiguous cases get one: a real
 *  agent id speaks for itself. */
function agentTitle(row: ErrorRow): string | undefined {
  if (!row.agent_id) return AGENT_UNKNOWN_NOTE;

  if (row.agent_id === UNTAGGED_AGENT) return UNTAGGED_NOTE;

  return undefined;
}

/** The applied facet as text, empty when nothing is selected. */
function describeFacet(facet: Facet): string {
  const parts: string[] = [];
  if (facet.status !== undefined) parts.push(String(facet.status));

  if (facet.provider !== undefined) parts.push(facet.provider);

  return parts.join(" · ");
}

export function FailuresPanel({
  open,
  onClose,
  onOpenAgent,
}: {
  open: boolean;
  onClose: () => void;
  /** Navigate to an agent's page. Omitted on the agent page itself, which
   *  leaves the agent rows as plain labels rather than links to where we are. */
  onOpenAgent?: (agentId: string) => void;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;

    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => prev?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        aria-hidden
        className="fixed inset-0 z-[60] bg-black/30"
        onClick={onClose}
      />
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Failed requests"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-[61] flex w-[860px] max-w-full flex-col outline-none"
        style={{
          background: "var(--surface)",
          borderLeft: "1px solid var(--border)",
          boxShadow: "-14px 0 36px rgba(0,0,0,0.13)",
        }}
      >
        <div
          className="flex flex-shrink-0 items-start justify-between gap-3 px-6 py-3.5"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div>
            <h2 className="tw-display text-[14px] font-semibold text-[var(--text)]">
              Failed requests
            </h2>
            <p className="mt-0.5 text-[10px] text-[var(--text-muted)]">
              Every call that came back 4xx or 5xx in the selected range, with
              the error the provider returned.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <FailuresBody onOpenAgent={onOpenAgent} />
        </div>
      </aside>
    </>
  );
}

function FailuresBody({
  onOpenAgent,
}: {
  onOpenAgent?: (agentId: string) => void;
}) {
  const [facet, setFacet] = useState<Facet>({});
  // The cursor stack is the caller's: the hook keys its cache on the stringified
  // params and holds no history, so "Previous" has to be a stack we own. Every
  // cursor is opaque — pushed and popped, never parsed.
  const [cursors, setCursors] = useState<string[]>([]);

  const facetParams = useMemo(
    () => ({
      ...(facet.status !== undefined ? { status: [facet.status] } : {}),
      ...(facet.provider !== undefined ? { provider: [facet.provider] } : {}),
    }),
    [facet],
  );

  const breakdown = useErrorBreakdown(facetParams);
  const page = useErrors({
    ...facetParams,
    limit: PAGE_SIZE,
    cursor: cursors[cursors.length - 1],
  });
  const facetLabel = describeFacet(facet);
  // Set on the agent page (this panel renders inside its AgentScopeProvider),
  // null on the workspace overview.
  const scopedAgent = useAgentScope();

  const selectFacet = (next: Facet) => {
    setFacet(next);
    setCursors([]);
  };

  return (
    <div className="flex flex-col gap-6">
      <BreakdownSections
        state={breakdown}
        facet={facet}
        scopedAgent={scopedAgent}
        onSelectFacet={selectFacet}
        onOpenAgent={onOpenAgent}
      />

      <section>
        <SectionTitle>
          Recent failures
          {facetLabel !== "" && (
            <>
              {/* Spelling the facet out is what keeps the affordance honest: a
                  click selects a status × provider slice, which is usually
                  wider than the one row it was picked from. */}
              <span
                className="tw-mono ml-2 text-[10px] font-normal"
                style={{ color: "var(--text-muted)" }}
              >
                filtered to {facetLabel}
              </span>
              <button
                type="button"
                onClick={() => selectFacet({})}
                className="ml-2 text-[11px] font-normal underline underline-offset-2 text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
              >
                Clear filter
              </button>
            </>
          )}
        </SectionTitle>
        <QueryBoundary state={page} skeleton={<BarsSkeleton rows={6} />}>
          {(res) =>
            // An unscoped answer under an agent scope is the workspace's
            // failures, not this agent's — labelling them as its own is worse
            // than not listing them.
            scopeApplied(res.meta, scopedAgent) ? (
              <>
                <FailureTable rows={res.data.items} />
                <Pager
                  shown={res.data.items.length}
                  // Every page but the last is a full PAGE_SIZE, and the last
                  // one has no Next — so the stack depth is the row offset. The
                  // cursor itself stays opaque; nothing here parses it.
                  offset={cursors.length * PAGE_SIZE}
                  total={res.data.total}
                  canPrev={cursors.length > 0}
                  canNext={res.data.next_cursor !== null}
                  onPrev={() => setCursors((c) => c.slice(0, -1))}
                  onNext={() =>
                    setCursors((c) =>
                      res.data.next_cursor ? [...c, res.data.next_cursor] : c,
                    )
                  }
                />
              </>
            ) : (
              <EmptyNote>{SCOPE_UNAVAILABLE}</EmptyNote>
            )
          }
        </QueryBoundary>
      </section>
    </div>
  );
}

/* The two aggregates. They come from an endpoint that lands with the BFF half of
 * OPSS-352, so a deployment where it doesn't exist yet degrades to a note and
 * leaves the per-call table — which the BFF has always served — working. */
function BreakdownSections({
  state,
  facet,
  scopedAgent,
  onSelectFacet,
  onOpenAgent,
}: {
  state: ReturnType<typeof useErrorBreakdown>;
  facet: Facet;
  /** The agent this panel is pinned to, or null on the workspace overview. */
  scopedAgent: string | null;
  onSelectFacet: (facet: Facet) => void;
  onOpenAgent?: (agentId: string) => void;
}) {
  if (state.initialLoading) return <BarsSkeleton rows={4} />;

  if (state.data === undefined) {
    return (
      <EmptyNote>
        The failure breakdown isn&apos;t available yet. The individual calls are
        listed below.
      </EmptyNote>
    );
  }

  if (!scopeApplied(state.data.meta, scopedAgent)) {
    return <EmptyNote>{SCOPE_UNAVAILABLE}</EmptyNote>;
  }

  const { by_signature, by_agent, total } = state.data.data;
  // The degraded breakdown is a live `total` over empty dimensions. An empty
  // dimension there means "not available", never "nothing failed" — printing
  // the latter one line under "N failed", and above a table of those very
  // failures, contradicts the reconciliation this drawer exists to provide.
  const dimensionEmpty =
    total > 0 ? DIMENSION_UNAVAILABLE : "No failures in this range.";

  return (
    <>
      <section>
        <SectionTitle>
          By error
          <span
            className="ml-2 tw-mono text-[10px] font-normal"
            style={{ color: "var(--text-muted)" }}
          >
            {formatCompact(total)} failed
          </span>
        </SectionTitle>
        {by_signature.length === 0 ? (
          <EmptyNote>{dimensionEmpty}</EmptyNote>
        ) : (
          <ul className="flex flex-col">
            {by_signature.map((row) => (
              <SignatureRow
                key={`${row.status_code}|${row.provider}|${row.error_signature}`}
                row={row}
                total={total}
                selected={
                  facet.status === row.status_code &&
                  facet.provider === row.provider
                }
                onSelect={() =>
                  onSelectFacet(
                    facet.status === row.status_code &&
                      facet.provider === row.provider
                      ? {}
                      : { status: row.status_code, provider: row.provider },
                  )
                }
              />
            ))}
          </ul>
        )}
      </section>

      <section>
        <SectionTitle>By agent</SectionTitle>
        {by_agent.length === 0 ? (
          <EmptyNote>{dimensionEmpty}</EmptyNote>
        ) : (
          <ul className="flex flex-col">
            {by_agent.map((row) => (
              <AgentRow
                key={row.agent_id}
                row={row}
                total={total}
                onOpen={onOpenAgent}
              />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function SignatureRow({
  row,
  total,
  selected,
  onSelect,
}: {
  row: ErrorBreakdownRow;
  total: number;
  selected: boolean;
  onSelect: () => void;
}) {
  // Names the slice, not the row: every row sharing this status and provider is
  // selected together, so "filter to this error" would be a lie. `title` only
  // supplements the accessible name — the row's own text stays the name a
  // screen reader announces.
  const facet = `${row.status_code} · ${row.provider}`;
  const title = selected
    ? `Showing every ${facet} failure. Click to clear.`
    : `Show every ${facet} failure`;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        title={title}
        className="flex w-full items-center gap-3 px-2 py-2 text-left transition-colors hover:bg-[var(--hover)]"
        style={{
          borderBottom: "1px solid var(--border)",
          background: selected ? "var(--hover)" : undefined,
        }}
      >
        <StatusPill status={row.status_code} />
        <span className="w-[92px] flex-shrink-0 truncate text-[12px] text-[var(--text)]">
          {row.provider}
        </span>
        <span className="w-[140px] flex-shrink-0 truncate text-[12px] text-[var(--text-muted)]">
          {row.error_type ?? "—"}
        </span>
        <span
          className="min-w-0 flex-1 truncate text-[11px] text-[var(--text-muted)]"
          title={row.sample_message ?? undefined}
        >
          {row.sample_message ?? "No message returned"}
        </span>
        <Count count={row.count} total={total} />
      </button>
    </li>
  );
}

function AgentRow({
  row,
  total,
  onOpen,
}: {
  row: ErrorAgentRow;
  total: number;
  onOpen?: (agentId: string) => void;
}) {
  const rowClass = "flex w-full items-center gap-3 px-2 py-2 text-left";
  const rowStyle = { borderBottom: "1px solid var(--border)" };
  const label = (
    <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--text)]">
      {row.agent_id}
    </span>
  );

  // `(untagged)` is a synthetic bucket, not an agent — there is no page to open,
  // and a row that navigates to a 404 is the trap here. It gets the explanation
  // instead of a link.
  if (row.agent_id === UNTAGGED_AGENT) {
    return (
      <li className={rowClass} style={rowStyle}>
        <span className="min-w-0 flex-1">
          <Tooltip side="bottom" align="start" content={UNTAGGED_NOTE}>
            {/* Focusable, and carrying the same text as a title. The bubble is
                portaled and hidden until hover or focus, so on a plain <span>
                the one row whose label doesn't explain itself is explained to
                mouse users only. Not a <button>: this row is deliberately not a
                way into an agent page that would 404. */}
            <span
              tabIndex={0}
              title={UNTAGGED_NOTE}
              className="cursor-help truncate text-[12px] text-[var(--text)] underline decoration-dotted underline-offset-2"
            >
              {row.agent_id}
            </span>
          </Tooltip>
        </span>
        <Count count={row.count} total={total} />
      </li>
    );
  }

  if (!onOpen) {
    return (
      <li className={rowClass} style={rowStyle}>
        {label}
        <Count count={row.count} total={total} />
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(row.agent_id)}
        className={`${rowClass} transition-colors hover:bg-[var(--hover)]`}
        style={rowStyle}
      >
        {label}
        <Count count={row.count} total={total} />
      </button>
    </li>
  );
}

function FailureTable({ rows }: { rows: ErrorRow[] }) {
  if (rows.length === 0)
    return <EmptyNote>No failures in this range.</EmptyNote>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr style={{ borderBottom: "1px solid var(--border)" }}>
            <Th>When</Th>
            <Th>Status</Th>
            <Th>Provider</Th>
            <Th>Error type</Th>
            <Th>Message</Th>
            <Th>Model</Th>
            <Th>Agent</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.request_id}
              className="transition-colors hover:bg-[var(--hover)]"
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <Td className="tw-mono whitespace-nowrap">
                {formatDateTimeUTC(row.created_at)}
              </Td>
              <Td>
                <StatusPill status={row.status_code} />
              </Td>
              {/* The BFF fills provider/error_type from the view; until it
                  deploys they are absent and every cell reads "—". */}
              <Td muted>{row.provider ?? "—"}</Td>
              <Td muted>{row.error_type ?? "—"}</Td>
              <Td
                muted
                className="max-w-[260px] truncate"
                title={row.error ?? undefined}
              >
                {row.error ?? "—"}
              </Td>
              <Td muted className="whitespace-nowrap">
                {row.model}
              </Td>
              {/* `(untagged)` is the server's own literal, so a row here and its
                  bucket above always read the same. A blank is NOT that: it means
                  this response can't attribute at all (raw-OTLP fallback), so it
                  reads "—" and says why on hover rather than claiming untagged. */}
              <Td muted title={agentTitle(row)}>
                {agentKey(row)}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pager({
  shown,
  offset,
  total,
  canPrev,
  canNext,
  onPrev,
  onNext,
}: {
  shown: number;
  offset: number;
  total: number | null;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (shown === 0) return null;

  const range = `${offset + 1}–${offset + shown}`;
  return (
    <div className="mt-3 flex items-center justify-between">
      <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
        Showing {range}
        {total === null ? "" : ` of ${formatCompact(total)}`}
      </span>
      <div className="flex gap-2">
        <PagerButton disabled={!canPrev} onClick={onPrev}>
          Previous
        </PagerButton>
        <PagerButton disabled={!canNext} onClick={onNext}>
          Next
        </PagerButton>
      </div>
    </div>
  );
}

function PagerButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-[2px] px-3 py-1.5 text-[11px] transition-colors hover:bg-[var(--hover)] disabled:cursor-not-allowed disabled:opacity-40"
      style={{ border: "1px solid var(--border-2)", color: "var(--text)" }}
    >
      {children}
    </button>
  );
}

/* 4xx and 5xx are different problems — a client/auth error is ours to fix, an
 * upstream 5xx is a provider incident — so they don't share a colour. */
function StatusPill({ status }: { status: number }) {
  const client = status < 500;
  return (
    <span
      className="tw-mono inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{
        background: client ? "var(--tint-amber)" : "var(--tint-crimson)",
        color: client ? "var(--val-amber)" : "var(--val-crimson)",
      }}
    >
      {status}
    </span>
  );
}

function Count({ count, total }: { count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <span className="tw-mono flex-shrink-0 text-[11px] tabular-nums text-[var(--text)]">
      {formatCompact(count)}
      <span className="ml-1.5" style={{ color: "var(--text-muted)" }}>
        {pct}%
      </span>
    </span>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <p
      className="mb-2 flex items-center text-[13px] leading-none"
      style={{ color: "var(--text)" }}
    >
      {children}
    </p>
  );
}

function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <p className="py-3 text-[12px]" style={{ color: "var(--text-muted)" }}>
      {children}
    </p>
  );
}

function Th({ children }: { children?: ReactNode }) {
  return (
    <th className="px-2 py-2 text-left text-[9px] font-semibold uppercase tracking-widest text-[var(--label)]">
      {children}
    </th>
  );
}

function Td({
  children,
  muted = false,
  className = "",
  title,
}: {
  children?: ReactNode;
  muted?: boolean;
  className?: string;
  title?: string;
}) {
  return (
    <td
      className={`px-2 py-2 ${className}`}
      style={{ color: muted ? "var(--text-muted)" : "var(--text)" }}
      title={title}
    >
      {children}
    </td>
  );
}
